import type { IframeContainerProps, InitiateCallMessage, AuthenticatedIframeContainerProps } from './types';
import { MobXProviderContext } from 'mobx-react';
import { useCallRouter } from 'Modules/softphone/hooks/useCallRouter';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Iframe from 'react-iframe-click';
import { getAuthToken } from '~/api';
import { ContentArea } from '../../components/shared/ContentArea';
import { RootStoreProps } from '../../stores/RootStore.types';
import { pushToGTMDataLayer } from '../../utils/analytics';
import { Styled } from './index.styles';


function isInitiateCallMessage(data: unknown): data is InitiateCallMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    (data as { type: unknown }).type === 'initiateCall'
  );
}

export const IframeContainer: React.FC<IframeContainerProps> = ({ src }) => (
  <Styled.IframeContainer>
    <ContentArea>
      <Iframe
        //@ts-ignore
        src={src}
        width="100%"
        height="99%"
        frameBorder={0}
        onInferredClick={() => {}} /* required by react-iframe-click */
      />
    </ContentArea>
  </Styled.IframeContainer>
);

export const AuthenticatedIframeContainer = ({ src, isBeingUsedInCommunicator = false }: AuthenticatedIframeContainerProps) => {
  const { person } = React.useContext<RootStoreProps>(MobXProviderContext);
  const { callWithPerson } = useCallRouter();
  const [token, setToken] = useState<string | null>(null);

  // Memoize the fetch token logic
  const fetchAuthToken = useCallback(async (): Promise<void> => {
    if (person?.IsLoggedIn) {
      setToken(getAuthToken() || null);
      return;
    }

    const authToken = await person.tryLoginFromLocalForage(false);
    setToken(authToken || null);
  }, [person]);

  // Use `useEffect` to fetch the token when the component mounts
  useEffect(() => {
    void fetchAuthToken();
  }, [fetchAuthToken]);

  // Use `useMemo` to compute the iframe source only when the `src` or `token` changes
  const iframeSrc = useMemo(() => {
    const url = new URL(src );

    if (isBeingUsedInCommunicator) {
      url.searchParams.set('isBeingUsedInCommunicator', 'true');
    }

    const finalUrl = url.toString();
    return token ? `${finalUrl}#${token}` : finalUrl;
  }, [src, token, isBeingUsedInCommunicator]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        if (isInitiateCallMessage(event.data)) {
          const phoneNumber = event.data.phoneNumber;
          const eventOrigin = event.data.eventOrigin;

          if (typeof phoneNumber === 'string' && phoneNumber.trim().length > 0) {
            void callWithPerson(undefined, phoneNumber.trim());

            if (eventOrigin === 'voicemailModal') {
              pushToGTMDataLayer('voicemail_call_back_modal');
            } else if (eventOrigin === 'expandedVoicemailRow') {
              pushToGTMDataLayer('voicemail_call_back_expand');
            }
          } else {
            console.warn('[IframeContainer] Invalid phone number received:', phoneNumber);
          }
        }
      } catch (error) {
        console.error('[IframeContainer] Error handling message:', error);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [src, callWithPerson]);

  return <IframeContainer key={token} src={iframeSrc} />;
};
